import type { Operation } from '@apollo/client/core';
import type {
    PrettyFn,
    PrettyColor,
} from '@precon-tools-suite-by-shared/pretty-console';
import type { Message } from 'graphql-ws';

// Pretty GraphQL definitions
export const pGraphQL: PrettyFn = ($) => $('GraphQL', $.neg('orange'));
export const pGraphQLLabel =
    (label: string): PrettyFn =>
    ($) =>
        pGraphQL($).s(label, $.pos('orange'));
export const pOperationType =
    (type: string, color: PrettyColor) =>
    (label: string): PrettyFn =>
    ($) =>
        $.s(type, $.neg(color)).s(label, $.pos(color));
export const pQuery = pOperationType('Query', 'orange');
export const pMutation = pOperationType('Mutation', 'pink');
export const pSubscription = pOperationType('Subscription', 'yellow');
export const pFragment = pOperationType('Fragment', 'bluegray');
export const pOperationLabel = (operation: Operation) => {
    const operationNode = operation.query.definitions[0];
    if (operationNode.kind === 'FragmentDefinition')
        return pFragment(operation.operationName);
    return (
        {
            query: pQuery,
            mutation: pMutation,
            subscription: pSubscription,
        } as any
    )[(operationNode as any).operation](operation.operationName);
};
export const pRequest: PrettyFn = ($) => $('REQUEST', $.bold);
export const pResponse: PrettyFn = ($) => $('RESPONSE', $.bold);

export const pOperation =
    (operation: Operation): PrettyFn =>
    ($) =>
        $.p(pOperationLabel(operation)).o(operation.variables);
export const pAt: PrettyFn = ($) => $.s('at', $.bold);
export const pNetworkErrorLabel: PrettyFn = ($) =>
    $('Network ERROR', $.bold, $.fg('red'));
export const pNetworkError =
    (networkError: any): PrettyFn =>
    ($) =>
        $.O(networkError);
export const pData =
    (data: any): PrettyFn =>
    ($) =>
        $.o(data);
export const pTimeMs =
    (ms: number): PrettyFn =>
    ($) =>
        $.s(String(ms), $.bold).s('ms', $.italic);

export const pLink: PrettyFn = ($) => $('ApolloLink', $.neg('bluegray'));

export const pLinkLabel =
    (label: string): PrettyFn =>
    ($) =>
        pLink($).s(label, $.pos('bluegray'));

// Pretty websocket logs.
export const pGraphQLWs: PrettyFn = ($) => $('graphql-ws', $.neg('purple'));
export const pGraphQLWsEvent =
    (event: string): PrettyFn =>
    ($) =>
        pGraphQLWs($).s(event, $.pos('purple'));
export const pWsCloseEvent =
    (event: CloseEvent): PrettyFn =>
    ($) =>
        $.s(
            `${event.code} `,
            $.bold,
            event.wasClean ? $.fg('red') : $.fg('green')
        ).s(` ${event.reason}`);
export const pWebSocket =
    (socket: WebSocket): PrettyFn =>
    ($) =>
        $.o(socket);
export const pWsMessage =
    (message: Message): PrettyFn =>
    ($) => {
        let res = $.s(`${message.type} `, $.bold, $.fg('purple'));
        if ('id' in message && typeof message.id === 'string') {
            res = res.s(`${message.id} `, $.italic, $.sm, $.fg('bluegray'));
        }

        if ('payload' in message) {
            return res.o(message.payload);
        } else {
            return res;
        }
    };
